export const CHECKOUT_PLANS = ['living', 'plus', 'wealth', 'advisor'] as const
export type CheckoutPlan = (typeof CHECKOUT_PLANS)[number]
export type PricingIntent = { plan: CheckoutPlan; payment: 'checkout' | 'card' }

export function readPricingIntent(search: string): PricingIntent | null {
  const params = new URLSearchParams(search)
  const plan = params.get('plan')
  if (params.getAll('plan').length !== 1 || !CHECKOUT_PLANS.includes(plan as CheckoutPlan)) return null
  const payment = params.get('payment') ?? 'checkout'
  if (params.getAll('payment').length > 1 || !['checkout', 'card'].includes(payment)) return null
  if (payment === 'card' && plan !== 'plus' && plan !== 'wealth') return null
  return { plan: plan as CheckoutPlan, payment: payment as PricingIntent['payment'] }
}

export function pricingReturnPath(raw: string | null): string | null {
  if (!raw || !/^\/(?:mycio\/)?pricing\?/.test(raw)) return null
  const url = new URL(raw, 'https://return.invalid')
  if (url.origin !== 'https://return.invalid' || url.hash || !['/pricing', '/mycio/pricing'].includes(url.pathname)) return null
  if ([...url.searchParams.keys()].some(key => key !== 'plan' && key !== 'payment')) return null
  const intent = readPricingIntent(url.search)
  return intent ? `/pricing?plan=${intent.plan}&payment=${intent.payment}` : null
}

export function pricingSignInHref(plan: CheckoutPlan, payment: PricingIntent['payment'] = 'checkout'): string {
  return `/sign-in?next=${encodeURIComponent(`/pricing?plan=${plan}&payment=${payment}`)}`
}

// Review R2/N7: the profile-delete re-auth step-up (ProfileDataPanels.tsx) sends a member through
// /sign-in and needs them back on the page they were on, not always /dashboard. Same shape and same
// discipline as `pricingReturnPath` above — a literal, bounded ALLOWLIST of first-party pathnames with
// no query string and no hash, checked against a sentinel base so an absolute URL, a protocol-relative
// `//host/...`, or a `javascript:`/`data:` scheme can never pass (`url.origin` would differ from the
// sentinel, same check `pricingReturnPath` relies on) — never a generic "return to any page" redirect.
const PROFILE_RETURN_PATHS = ['/dashboard', '/settings'] as const

export function profileReturnPath(raw: string | null): string | null {
  if (!raw || !raw.startsWith('/')) return null
  // Review R3-2: unlike `pricingReturnPath`'s regex pre-filter (which rejects anything not literally
  // starting with `/pricing?` or `/mycio/pricing?` before ever touching `new URL`), this function's own
  // pre-filter only checks a leading `/` — so a crafted value that still starts with `/` but is not a
  // valid relative URL (e.g. a bare `?next=//[`, a malformed IPv6-literal-shaped authority) reaches
  // `new URL()` and THROWS, uncaught, straight out of this function. Both callers of this function
  // (safeReturnPath, from /sign-in's own redirect effect and /auth/callback's landing decision) run on
  // live, attacker-reachable query-string input, so a throw here would have crashed sign-in itself, not
  // merely failed to find a safe destination. Caught and treated as "not a safe destination", same as
  // every other rejection path.
  try {
    const url = new URL(raw, 'https://return.invalid')
    if (url.origin !== 'https://return.invalid' || url.hash || url.search) return null
    return (PROFILE_RETURN_PATHS as readonly string[]).includes(url.pathname) ? url.pathname : null
  } catch {
    return null
  }
}

/** Tries every known first-party `next=` destination in turn. AuthFlow uses this instead of
 *  `pricingReturnPath` directly so a new safe destination is added here once, not at every call site. */
export function safeReturnPath(raw: string | null): string | null {
  return pricingReturnPath(raw) ?? profileReturnPath(raw)
}
